import { useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
  Link as RouterLink,
} from 'react-router';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react';
import {
  WiseThemeProvider,
  WiseSignIn,
  WiseShell,
  WiseChrome,
  WiseTopNav,
  WiseMobileDrawer,
  useWiseMobile,
  UserMenu,
  useWiseTheme,
  type WiseBrand,
  type WiseNavItem,
  type WiseLinkProps,
} from '@wise/ui';
import { useUserPhoto } from './auth/useUserPhoto';
import { apiScopes } from './auth/msalConfig';
import { arrivalLoginHint, claimAutoSignIn, markSignedOut, shouldSignInAutomatically, signedInAccount } from './auth/ssoMemory';
import { isAuthResponse, linkLoginHint } from './auth/linkHint';
import { MeProvider, useMe } from './lib/me';
import { DashboardPage } from './pages/Dashboard';
import { CasesPage } from './pages/Cases';
import { CaseDetailPage } from './pages/CaseDetail';
import { AdminModulesPage } from './pages/AdminModules';
import { AuditsPage } from './pages/Audits';
import { AuditRunPage } from './pages/AuditRun';
import { RecentCasesDropdown } from './components/RecentCasesDropdown';

// The Dial: wise-iam's control plane publishes the platform brand tokens to
// this blob; WiseThemeProvider fetches it at runtime and fail-softs to the
// baked design-system defaults if absent. Per-env: the pipeline bakes
// VITE_WISE_DIAL_URL (dev+test → UI-dev blob, prod → UI-prod blob per the Dial
// scoping). Fallback = the dev blob, so local `npm run dev` themes too.
const WISE_DIAL_URL =
  (import.meta.env.VITE_WISE_DIAL_URL as string | undefined) ??
  'https://stwiseiamdev.blob.core.windows.net/dial/tokens.json';

// The WISE chip in the header goes home to the WISE Launchpad
// (https://wise.westportproperties.net) in PRODUCTION only. Production is
// recognised by its custom domain: a module is given a *.westportproperties.net
// or *.usstorage.net hostname at its prod cutover and never before, while dev
// and test serve from *.azurewebsites.net. Everywhere else the chip is a plain
// label. It never points at wise-shell — that is the TEST home, and linking it
// from every module sent department heads into QC from production.
const WISE_HOME_URL = /\.(westportproperties|usstorage)\.net$/i.test(window.location.hostname)
  ? 'https://wise.westportproperties.net'
  : undefined;

// Per-app brand lockup ASSETS (the design system ships fonts + tokens, not
// logos). Navy chrome → the light (white) variant of each brand mark.
const BRAND_LOCKUP: Record<WiseBrand, string> = {
  WPI: '/brands/wpi/logo-light.png',
  USSC: '/brands/ussc/logo-light.svg',
};

// react-router Link adapted to @wise/ui's structural WiseLinkProps (the package
// never imports react-router itself).
function WiseRouterLink({ to, children, ...rest }: WiseLinkProps) {
  return (
    <RouterLink to={to} {...rest}>
      {children}
    </RouterLink>
  );
}

function isNavActive(pathname: string, to: string): boolean {
  // Cases owns its detail sub-routes; the rest match exactly.
  if (to === '/cases') return pathname === '/cases' || pathname.startsWith('/cases/');
  return pathname === to;
}

function pageTitle(pathname: string): string {
  if (pathname === '/dashboard') return 'Dashboard';
  if (pathname === '/cases') return 'Active cases';
  if (pathname.startsWith('/cases/')) return 'Case';
  if (pathname === '/admin/modules') return 'Modules';
  if (pathname.startsWith('/audits')) return 'Audits';
  return '';
}

/**
 * The standardized WISE app shell — navy chrome with the
 * `[Brand] | WISE | Quality Control | [Page]` breadcrumb, a grouped left
 * rail, and the data-driven account menu. The Modules admin entry only
 * renders for me.canManage (the route + API gate server-side regardless).
 */
function AppShell({ children }: { children: React.ReactNode }) {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const { brand } = useWiseTheme();
  const photoUrl = useUserPhoto();
  const me = useMe();

  // The ACTIVE account — the link's person, or whoever just signed in — never
  // simply the first cached account (auth/ssoMemory.ts).
  const account = signedInAccount(instance, accounts);
  const email = account?.username ?? '';
  const name = account?.name ?? email;

  // Top navigation (compact module). Modules tab appears only for managers.
  const navItems: WiseNavItem[] = [
    { to: '/dashboard', label: 'Dashboard', section: 'quality control' },
    { to: '/cases', label: 'Cases', section: 'quality control' },
    ...(me?.canManage
      ? [
          { to: '/audits', label: 'Audits', section: 'quality control' } satisfies WiseNavItem,
          { to: '/admin/modules', label: 'Modules', section: 'admin' } satisfies WiseNavItem,
        ]
      : []),
  ];

  // Mobile nav (≤768px via @wise/ui's useWiseMobile — same pattern as the
  // CMMS portal): drop the top tab row, compact the chrome, and move nav into
  // a hamburger + off-canvas WiseMobileDrawer. Desktop keeps the tab row.
  const isMobile = useWiseMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Close the drawer on navigation (the drawer also closes on item tap).
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <WiseShell
      contentPadding={isMobile ? 14 : 24}
      chrome={
        <WiseChrome
          lockup={
            <img
              src={BRAND_LOCKUP[brand]}
              alt={brand}
              style={{ height: 26, display: 'block' }}
            />
          }
          system="Quality Control"
          page={pageTitle(location.pathname)}
          compact={isMobile}
          onMenuClick={isMobile ? () => setDrawerOpen(true) : undefined}
          onLockupClick={() => navigate('/dashboard')}
          wiseShellUrl={WISE_HOME_URL}
          loginHint={email}
          right={
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <RecentCasesDropdown />
            <UserMenu
              user={{ name, email, photoUrl }}
              items={[
                {
                  label: 'Sign out',
                  danger: true,
                  // A CHOICE: remembered, so no link signs them straight
                  // back in (auth/ssoMemory.ts) — until they sign in again.
                  onClick: () => {
                    markSignedOut();
                    void instance.logoutRedirect({
                      account,
                      postLogoutRedirectUri: window.location.origin,
                    });
                  },
                },
              ]}
            />
            </div>
          }
        />
      }
      topNav={
        isMobile ? undefined : (
          <WiseTopNav
            items={navItems}
            linkComponent={WiseRouterLink}
            isActive={(to) => isNavActive(location.pathname, to)}
            maxWidth={1200}
          />
        )
      }
    >
      <WiseMobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        items={navItems}
        linkComponent={WiseRouterLink}
        isActive={(to) => isNavActive(location.pathname, to)}
      />
      {/* Preserve the centered max-width content column; remount on route
          change so per-page state resets the way it did before. */}
      <div key={location.pathname} style={{ maxWidth: 1200, margin: '0 auto' }}>
        {children}
      </div>
    </WiseShell>
  );
}

// Never from the hidden renewal iframe or the page carrying Entra's answer.
const automaticSignInAllowedHere = (): boolean =>
  window.self === window.top && !isAuthResponse(window.location.search, window.location.hash);

function SignInGate() {
  const { instance, inProgress } = useMsal();
  // NEVER A SIGN-IN PAGE FOR SOMEONE WITH AN ENTRA SESSION (the platform rule,
  // Chauncey White 2026-10-23: "EVERY link emailed or sent or coded should
  // check to see if the user is logged in"). Go straight to Entra — with the
  // link's login_hint (auth/linkHint.ts: in the query, or inside the hash),
  // else whoever last signed in on this browser — ONCE per tab (a round trip
  // that comes back without a session lands here and gets the page), never
  // after a chosen Sign out, and only while MSAL is idle
  // (`interaction_in_progress` would burn the one try). Seeded during render
  // so the sign-in card never flashes before the redirect leaves.
  const [redirecting, setRedirecting] = useState(() => automaticSignInAllowedHere() && shouldSignInAutomatically());
  useEffect(() => {
    if (inProgress !== 'none') return;
    if (!automaticSignInAllowedHere() || !shouldSignInAutomatically() || !claimAutoSignIn()) return;
    const hint = arrivalLoginHint(linkLoginHint(window.location.search, window.location.hash));
    instance.loginRedirect({ scopes: apiScopes, ...(hint ? { loginHint: hint } : {}) }).catch((err: unknown) => {
      // The redirect could not start: the sign-in page, never a stuck splash.
      console.warn('[itms-qc] automatic sign-in redirect failed:', err);
      setRedirecting(false);
    });
  }, [instance, inProgress]);

  if (redirecting) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-text-muted, #8BA4B8)' }}>
        Signing you in…
      </div>
    );
  }
  // Standard WISE arrival surface (same component every module uses).
  return (
    <WiseSignIn
      title="Quality Control"
      tagline="File bugs and feature requests against any WISE module — AI helps spot duplicates as you type."
      logos={['/brands/wpi/logo-dark.png', '/brands/ussc/logo-dark.svg']}
      logoAlts={['Westport Properties', 'US Storage Centers']}
      onSignIn={() => void instance.loginRedirect({ scopes: apiScopes })}
    />
  );
}

export function App() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress, accounts } = useMsal();
  const activeEmail = signedInAccount(instance, accounts)?.username ?? null;

  // While MSAL is resolving the redirect from the auth provider on first load,
  // show a minimal loading state instead of flashing the sign-in gate.
  const settling = inProgress !== 'none' && !isAuthenticated;

  return (
    // WiseThemeProvider sets data-brand on <html> (resolved from the signed-in
    // user's email domain) so @wise/design-system supplies the brand --brand-*
    // tokens every @wise/ui surface consumes. Pre-auth it falls back to WPI.
    <WiseThemeProvider userEmail={activeEmail} tokensUrl={WISE_DIAL_URL} moduleId="itms-qc">
      {settling ? (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--brand-text-muted, #8BA4B8)',
          }}
        >
          Signing you in…
        </div>
      ) : (
        <>
          <UnauthenticatedTemplate>
            <SignInGate />
          </UnauthenticatedTemplate>
          <AuthenticatedTemplate>
            <MeProvider>
              <Router>
                <AppShell>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/cases" element={<CasesPage />} />
                    <Route path="/cases/:id" element={<CaseDetailPage />} />
                    <Route path="/audits" element={<AuditsPage />} />
                    <Route path="/audits/runs/:runId" element={<AuditRunPage />} />
                    <Route path="/admin/modules" element={<AdminModulesPage />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </AppShell>
              </Router>
            </MeProvider>
          </AuthenticatedTemplate>
        </>
      )}
    </WiseThemeProvider>
  );
}
